import { Label } from '@/components/ui/label'
import { billingsFiscalMonths, billingsFiscalQuarters } from '@/lib/billings-fiscal'
import type { BillingsGranularity } from '@/lib/billings-fiscal'
import type { BillingsFiscalYear } from '@/types'

interface FiscalYearPickerProps {
  years: BillingsFiscalYear[]
  value?: number
  onChange: (fy: number) => void
  /**
   * Omit to render the year control alone, which is what the Targets page wants: it is an
   * annual grid, and narrowing it to a month would leave a page that cannot express what
   * it is for.
   */
  period?: {
    granularity: BillingsGranularity
    month?: string
    quarter?: number
    onChange: (next: {
      granularity?: BillingsGranularity
      month?: string
      quarter?: number
    }) => void
  }
}

const SELECT_CLASS = 'mt-1 block h-9 rounded-md border border-input bg-background px-3 text-sm'

/**
 * The period control, shared by every Sales Trackers page.
 *
 *
 * WHICH YEARS ARE OFFERED, AND WHICH MONTHS
 *
 * Years come from the server, which offers any year holding billings OR targets. Months are
 * all twelve of the chosen year, unconditionally, and that difference is deliberate. A year
 * nobody has touched at all is noise in a dropdown; a MONTH with a target but no invoices
 * yet is the single most useful thing on this page, because it answers "how am I tracking".
 * Restricting months to those holding billings would have hidden exactly that.
 *
 * A year that carries targets but no billings reports "no billings yet" beside it rather
 * than a line count, so the list never implies an upload has happened.
 *
 *
 * WHY THERE IS NO LONGER AN "AMOUNTS" TOGGLE
 *
 * This component used to offer a second select, 'USD (converted)' against 'As imported
 * (sign only)', which chose whether the backend summed `amount_usd` or
 * `amount_normalised`. It was removed, and the reason is arithmetic rather than taste.
 *
 * `amount_normalised` is `amount_native * sign_multiplier` with no FX applied. The NA
 * ledger is denominated in CAD and the AU ledger in AUD, so a company total on that
 * basis adds AUD to CAD as though the rate were 1:1. Measured on real data, FY2026 read
 * $6,477,061 that way against $6,431,004 converted, the $46,057 gap being 10% of the
 * $460,571 AU portion.
 *
 * So the second option was not another view of the same money, it was a sum of two
 * currencies. It also tied to nothing: the old comment here claimed it was the figure
 * that reconciled to Jason's workbook, but the workbook applies the 0.9 AU rate itself,
 * so the CONVERTED basis is what reproduces his numbers. Removing the control removes a
 * wrong number, not a cross-check.
 *
 * The backend still accepts `?basis=`, still defaults to converted, and `amount_usd` is
 * still the column every page reads. Nothing about the displayed figures changed when
 * the control went, because converted was already the default.
 *
 * Note also that `amount_usd` and `fx_to_usd` are misnamed: there is not one USD row in
 * the data, the money is CAD, and 0.9 is an AUD to CAD rate. Renaming them means
 * rebuilding the view and every aggregate, so it is recorded as debt rather than done.
 */
export function FiscalYearPicker({ years, value, onChange, period }: FiscalYearPickerProps) {
  const months = value ? billingsFiscalMonths(value) : []
  const quarters = value ? billingsFiscalQuarters(value) : []

  return (
    <div className="flex flex-wrap items-end gap-4">
      <div>
        <Label htmlFor="fy-select">Fiscal year</Label>
        <select
          id="fy-select"
          className={SELECT_CLASS}
          // The helper text below was previously unassociated, so a screen reader never
          // heard the one thing a reader needs to know: that this year is not a calendar
          // year.
          aria-describedby="fy-help"
          value={value ?? ''}
          onChange={(e) => onChange(Number(e.target.value))}
        >
          {years.map((y) => (
            <option key={y.fiscal_year} value={y.fiscal_year}>
              {y.label}{' '}
              {y.line_count > 0 ? `(${y.line_count.toLocaleString()} lines)` : '(targets only)'}
            </option>
          ))}
        </select>
        <p id="fy-help" className="text-xs text-muted-foreground mt-1">
          March to February. Amounts are CAD.
        </p>
      </div>

      {period && (
        <>
          <div>
            <Label htmlFor="period-select">Show</Label>
            <select
              id="period-select"
              className={SELECT_CLASS}
              value={period.granularity}
              onChange={(e) =>
                period.onChange({
                  granularity: e.target.value as BillingsGranularity,
                })
              }
            >
              <option value="month">A month</option>
              <option value="quarter">A quarter</option>
              <option value="year">The whole year</option>
            </select>
          </div>

          {period.granularity === 'month' && (
            <div>
              <Label htmlFor="month-select">Month</Label>
              <select
                id="month-select"
                className={`${SELECT_CLASS} min-w-[10rem]`}
                value={period.month ?? ''}
                onChange={(e) => period.onChange({ month: e.target.value })}
              >
                {months.map((m) => (
                  <option key={m.key} value={m.key}>
                    {m.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {period.granularity === 'quarter' && (
            <div>
              <Label htmlFor="quarter-select">Quarter</Label>
              <select
                id="quarter-select"
                className={`${SELECT_CLASS} min-w-[14rem]`}
                value={period.quarter ?? ''}
                onChange={(e) => period.onChange({ quarter: Number(e.target.value) })}
              >
                {quarters.map((q) => (
                  <option key={q.quarter} value={q.quarter}>
                    {q.label}
                  </option>
                ))}
              </select>
            </div>
          )}
        </>
      )}
    </div>
  )
}
